import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { COLLECTIONS } from "../utils/constants";

/**
 * Firestore service for managing registrations and user accounts
 * Implements CRUD operations with proper error handling and data validation
 */
class FirestoreService {
  /**
   * Creates a new registration record in Firestore
   * @param {Object} data - Registration data containing userInfo, representativeInfo, etc.
   * @returns {Promise<string>} - Document ID of the created registration
   * @throws {Error} - If creation fails or data is invalid
   */
  async createRegistration(data) {
    try {
      // Validate required data structure
      if (!data.userInfo || !data.userInfo.email) {
        throw new Error("Registration data must include user email");
      }

      if (!data.representativeInfo) {
        throw new Error(
          "Registration data must include representative information",
        );
      }

      // Check for email uniqueness before creating
      const emailExists = await this.checkEmailExists(data.userInfo.email);
      if (emailExists) {
        throw new Error("Email address is already registered");
      }

      // Check for phone number uniqueness before creating
      const phoneExists = await this.checkPhoneExists(
        data.userInfo.mobileNumber,
      );
      if (phoneExists) {
        throw new Error("Phone number is already registered");
      }

      // Handle referrer information if provided
      let referrerInfo = null;
      if (
        data.userInfo.referrerContact &&
        data.userInfo.referrerContact.trim()
      ) {
        const referrer = await this.checkReferrerExists(
          data.userInfo.referrerContact.trim(),
        );
        if (referrer) {
          referrerInfo = {
            referrerId: referrer.id,
            referrerName: referrer.fullName,
            referrerEmail: referrer.email,
            referrerPhone: referrer.mobileNumber,
            referredAt: serverTimestamp(),
          };
        }
      }

      const registrationData = {
        ...data,
        referrerInfo, // Add referrer information
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Store lowercase username for case-insensitive uniqueness queries
      if (data.userInfo?.username) {
        registrationData.userInfo = {
          ...registrationData.userInfo,
          usernameLower: data.userInfo.username.toLowerCase().trim(),
        };
      }

      const docRef = await addDoc(
        collection(db, "registrations"),
        registrationData,
      );
      return docRef.id;
    } catch (error) {
      console.error("Error creating registration:", error);
      throw new Error(`Failed to create registration: ${error.message}`);
    }
  }

  /**
   * Retrieves all registration records, ordered by creation date
   * @returns {Promise<Array>} - Array of registration records with IDs
   * @throws {Error} - If retrieval fails
   */
  async getRegistrations() {
    try {
      const q = query(
        collection(db, COLLECTIONS.REGISTRATIONS),
        orderBy("createdAt", "desc"),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error("Error fetching registrations:", error);
      throw new Error(`Failed to fetch registrations: ${error.message}`);
    }
  }

  /**
   * Retrieves a single registration by ID
   * @param {string} id - Registration document ID
   * @returns {Promise<Object|null>} - Registration data or null if not found
   * @throws {Error} - If retrieval fails
   */
  async getRegistrationById(id) {
    try {
      const docRef = doc(db, "registrations", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching registration by ID:", error);
      throw new Error(`Failed to fetch registration: ${error.message}`);
    }
  }

  /**
   * Updates registration status with optional admin remark
   * @param {string} id - Registration document ID
   * @param {string} status - New status ('approved', 'rejected', 'pending')
   * @param {string|null} remark - Optional admin remark for rejections
   * @returns {Promise<void>}
   * @throws {Error} - If update fails or status is invalid
   */
  async updateRegistrationStatus(id, status, remark = null) {
    try {
      // Validate status
      const validStatuses = ["pending", "approved", "rejected"];
      if (!validStatuses.includes(status)) {
        throw new Error(
          `Invalid status: ${status}. Must be one of: ${validStatuses.join(", ")}`,
        );
      }

      const docRef = doc(db, "registrations", id);
      const updateData = {
        status,
        updatedAt: serverTimestamp(),
      };

      if (remark) {
        updateData.adminRemark = remark;
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error("Error updating registration status:");
      throw new Error(`Failed to update registration status: ${error.message}`);
    }
  }

  /**
   * Creates a user account with temporary password
   * Uses email as document ID for easy lookup
   * @param {string} email - User email address
   * @param {string} password - Temporary password
   * @param {string} registrationId - Reference to the registration document
   * @returns {Promise<void>}
   * @throws {Error} - If account creation fails
   */
  async createUserAccount(email, password, registrationId = null) {
    try {
      if (!email || !password) {
        throw new Error(
          "Email and password are required for user account creation",
        );
      }

      const userAccountData = {
        email,
        temporaryPassword: password,
        passwordChanged: false,
        isAdmin: false,
        isModerator: false,
        createdAt: serverTimestamp(),
      };

      if (registrationId) {
        userAccountData.registrationId = registrationId;
      }

      const docRef = doc(db, "user_accounts", email);
      await setDoc(docRef, userAccountData);
    } catch (error) {
      console.error("Error creating user account:", error);
      throw new Error(`Failed to create user account: ${error.message}`);
    }
  }

  /**
   * Retrieves user account by email
   * @param {string} email - User email address
   * @returns {Promise<Object|null>} - User account data or null if not found
   * @throws {Error} - If retrieval fails
   */
  async getUserAccount(email) {
    try {
      const docRef = doc(db, "user_accounts", email);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching user account:", error);
      throw new Error(`Failed to fetch user account: ${error.message}`);
    }
  }

  /**
   * Updates user account password and sets passwordChanged flag
   * @param {string} email - User email address
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   * @throws {Error} - If update fails
   */
  async updateUserPassword(email, newPassword) {
    try {
      const docRef = doc(db, "user_accounts", email);
      // Only mark passwordChanged = true. Never overwrite temporaryPassword
      // (it's the initial credential used for re-auth — overwriting it breaks re-auth)
      await updateDoc(docRef, {
        passwordChanged: true,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating user password:", error);
      throw new Error(`Failed to update user password: ${error.message}`);
    }
  }

  /**
   * Checks if an email address is already registered
   * @param {string} email - Email address to check
   * @returns {Promise<boolean>} - True if email exists, false otherwise
   * @throws {Error} - If check fails
   */
  async checkEmailExists(email) {
    try {
      if (!email) {
        throw new Error("Email is required for uniqueness check");
      }

      const q = query(
        collection(db, COLLECTIONS.REGISTRATIONS),
        where("userInfo.email", "==", email),
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error checking email existence:", error);
      throw new Error("Failed to check email availability. Please try again.");
    }
  }

  /**
   * Checks if a phone number is already registered
   * @param {string} phoneNumber - Phone number to check
   * @returns {Promise<boolean>} - True if phone number exists, false otherwise
   * @throws {Error} - If check fails
   */
  async checkPhoneExists(phoneNumber) {
    try {
      if (!phoneNumber) {
        throw new Error("Phone number is required for uniqueness check");
      }

      const q = query(
        collection(db, COLLECTIONS.REGISTRATIONS),
        where("userInfo.mobileNumber", "==", phoneNumber),
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error checking phone number existence:", error);
      throw new Error(
        "Failed to check phone number availability. Please try again.",
      );
    }
  }

  /**
   * Checks if a referrer exists in the system by email or phone number
   * @param {string} contact - Email or phone number to check
   * @returns {Promise<Object|null>} - Referrer info if found, null otherwise
   * @throws {Error} - If check fails
   */
  async checkReferrerExists(contact) {
    try {
      if (!contact) {
        return null;
      }

      const trimmedContact = contact.trim();

      // Check if it's an email (contains @) or phone number
      const isEmail = trimmedContact.includes("@");

      // Query approved registrations for referrer
      const q = query(
        collection(db, "registrations"),
        where("status", "==", "approved"),
        where(
          isEmail ? "userInfo.email" : "userInfo.mobileNumber",
          "==",
          trimmedContact,
        ),
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const referrerDoc = querySnapshot.docs[0];
        const referrerData = referrerDoc.data();
        return {
          id: referrerDoc.id,
          fullName: referrerData.userInfo.fullName,
          email: referrerData.userInfo.email,
          mobileNumber: referrerData.userInfo.mobileNumber,
          country: referrerData.userInfo.country,
          city: referrerData.userInfo.city,
        };
      }

      return null;
    } catch (error) {
      console.error("Error checking referrer existence:", error);
      throw new Error(`Failed to validate referrer: ${error.message}`);
    }
  }

  /**
   * Performs atomic registration approval with user account creation
   * @param {string} registrationId - Registration document ID
   * @param {string} temporaryPassword - Generated temporary password
   * @returns {Promise<void>}
   * @throws {Error} - If transaction fails
   */
  async approveRegistrationWithAccount(registrationId, temporaryPassword) {
    try {
      await runTransaction(db, async (transaction) => {
        // Get registration document
        const registrationRef = doc(db, "registrations", registrationId);
        const registrationDoc = await transaction.get(registrationRef);

        if (!registrationDoc.exists()) {
          throw new Error("Registration not found");
        }

        const registrationData = registrationDoc.data();
        const userEmail = registrationData.userInfo.email;

        // Update registration status
        transaction.update(registrationRef, {
          status: "approved",
          updatedAt: serverTimestamp(),
        });

        // Create user account
        const userAccountRef = doc(db, "user_accounts", userEmail);
        const accountData = {
          email: userEmail,
          temporaryPassword,
          passwordChanged: false,
          isAdmin: false,
          isModerator: false,
          createdAt: serverTimestamp(),
          registrationId,
        };
        // Copy username if present in the registration
        if (registrationData.userInfo?.username) {
          accountData.username = registrationData.userInfo.username;
          accountData.usernameLower =
            registrationData.userInfo.username.toLowerCase();
        }
        transaction.set(userAccountRef, accountData);
      });
    } catch (error) {
      console.error("Error in approval transaction:", error);
      throw new Error(`Failed to approve registration: ${error.message}`);
    }
  }

  /**
   * Get the cityId for a user from their registration record.
   * Falls back to user_accounts.cityId if not found in registrations.
   */
  async getUserCityId(email) {
    try {
      const q = query(
        collection(db, COLLECTIONS.REGISTRATIONS),
        where("userInfo.email", "==", email),
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const cityId = snap.docs[0].data()?.userInfo?.cityId;
        if (cityId) return cityId;
      }
      // Fallback: user_accounts.cityId
      const account = await this.getUserAccount(email);
      return account?.cityId || null;
    } catch (error) {
      console.error("Error getting user cityId:", error);
      return null;
    }
  }

  /**
   * Set or revoke admin/moderator role for a user account
   * @param {string} email - User email (document ID in user_accounts)
   * @param {'isAdmin'|'isModerator'} roleField - The role field to update
   * @param {boolean} value - true to assign, false to revoke
   */
  async setUserRole(email, roleField, value) {
    try {
      const docRef = doc(db, "user_accounts", email);
      await updateDoc(docRef, {
        [roleField]: value,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating user role:", error);
      throw new Error(`Failed to update user role: ${error.message}`);
    }
  }

  /**
   * Gets registration statistics for admin dashboard
   * @returns {Promise<Object>} - Statistics object with counts by status
   * @throws {Error} - If retrieval fails
   */
  async getRegistrationStats() {
    try {
      const querySnapshot = await getDocs(collection(db, "registrations"));
      const stats = {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
      };

      querySnapshot.docs.forEach((doc) => {
        const data = doc.data();
        stats.total++;
        stats[data.status] = (stats[data.status] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error("Error fetching registration stats:", error);
      throw new Error(
        `Failed to fetch registration statistics: ${error.message}`,
      );
    }
  }
  /**
   * Gets comprehensive referral reports and analytics
   * @returns {Promise<Object>} - Referral data including stats, top referrers, and network
   * @throws {Error} - If retrieval fails
   */
  async getReferralReports() {
    try {
      // Get all registrations
      const registrations = await this.getRegistrations();

      // Filter registrations with referrer info
      const referredRegistrations = registrations.filter(
        (reg) => reg.referrerInfo,
      );

      // Calculate statistics
      const referralStats = {
        totalReferrals: referredRegistrations.length,
        totalReferrers: new Set(
          referredRegistrations.map((reg) => reg.referrerInfo.referrerId),
        ).size,
        approvedReferrals: referredRegistrations.filter(
          (reg) => reg.status === "approved",
        ).length,
        pendingReferrals: referredRegistrations.filter(
          (reg) => reg.status === "pending",
        ).length,
        rejectedReferrals: referredRegistrations.filter(
          (reg) => reg.status === "rejected",
        ).length,
      };

      // Group by referrer to get top referrers
      const referrerMap = new Map();

      referredRegistrations.forEach((registration) => {
        const referrerId = registration.referrerInfo.referrerId;

        if (!referrerMap.has(referrerId)) {
          referrerMap.set(referrerId, {
            id: referrerId,
            name: registration.referrerInfo.referrerName,
            email: registration.referrerInfo.referrerEmail,
            phone: registration.referrerInfo.referrerPhone,
            city: "", // Will be filled from referrer's own registration
            country: "",
            totalReferrals: 0,
            approvedReferrals: 0,
            pendingReferrals: 0,
            rejectedReferrals: 0,
            referrals: [],
            joinedAt: null,
          });
        }

        const referrer = referrerMap.get(referrerId);
        referrer.totalReferrals++;

        if (registration.status === "approved") referrer.approvedReferrals++;
        else if (registration.status === "pending") referrer.pendingReferrals++;
        else if (registration.status === "rejected")
          referrer.rejectedReferrals++;

        referrer.referrals.push({
          id: registration.id,
          name: registration.userInfo.fullName,
          email: registration.userInfo.email,
          status: registration.status,
          date:
            registration.createdAt?.toDate?.() ||
            new Date(registration.createdAt),
        });
      });

      // Get referrer details from their own registrations
      for (const [referrerId, referrer] of referrerMap) {
        const referrerRegistration = registrations.find(
          (reg) => reg.id === referrerId,
        );
        if (referrerRegistration) {
          referrer.city = referrerRegistration.userInfo.city;
          referrer.country = referrerRegistration.userInfo.country;
          referrer.joinedAt =
            referrerRegistration.createdAt?.toDate?.() ||
            new Date(referrerRegistration.createdAt);
        }
      }

      // Calculate success rates and sort top referrers
      const topReferrers = Array.from(referrerMap.values())
        .map((referrer) => ({
          ...referrer,
          successRate:
            referrer.totalReferrals > 0
              ? Math.round(
                  (referrer.approvedReferrals / referrer.totalReferrals) * 100,
                )
              : 0,
        }))
        .sort((a, b) => {
          // Sort by total referrals first, then by success rate
          if (b.totalReferrals !== a.totalReferrals) {
            return b.totalReferrals - a.totalReferrals;
          }
          return b.successRate - a.successRate;
        })
        .slice(0, 10); // Top 10 referrers

      // Recent referrals (last 50)
      const recentReferrals = referredRegistrations
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA;
        })
        .slice(0, 50)
        .map((registration) => ({
          id: registration.id,
          referredUserName: registration.userInfo.fullName,
          referredUserEmail: registration.userInfo.email,
          referredUserCity: registration.userInfo.city,
          referredUserCountry: registration.userInfo.country,
          referrerName: registration.referrerInfo.referrerName,
          referrerEmail: registration.referrerInfo.referrerEmail,
          referredAt:
            registration.createdAt?.toDate?.() ||
            new Date(registration.createdAt),
          status: registration.status,
        }));

      // Referral network (for visualization)
      const referralNetwork = Array.from(referrerMap.values())
        .filter((referrer) => referrer.totalReferrals > 0)
        .slice(0, 20) // Limit for performance
        .map((referrer) => ({
          id: referrer.id,
          name: referrer.name,
          email: referrer.email,
          referralCount: referrer.totalReferrals,
          referrals: referrer.referrals.slice(0, 5), // Show max 5 referrals per node
        }));

      return {
        referralStats,
        topReferrers,
        recentReferrals,
        referralNetwork,
      };
    } catch (error) {
      console.error("Error generating referral reports:", error);
      throw new Error(`Failed to generate referral reports: ${error.message}`);
    }
  }
  /**
   * Create a password reset token (expires in 10 minutes)
   */
  async createPasswordResetToken(email) {
    try {
      // Invalidate any existing tokens for this email
      const existing = query(
        collection(db, "password_reset_tokens"),
        where("email", "==", email),
        where("used", "==", false),
      );
      const snap = await getDocs(existing);
      for (const d of snap.docs) {
        await updateDoc(doc(db, "password_reset_tokens", d.id), { used: true });
      }

      // Generate a secure random token
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      const token = Array.from(array)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

      const expiresAt = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes

      await addDoc(collection(db, "password_reset_tokens"), {
        email,
        token,
        expiresAt,
        used: false,
        createdAt: serverTimestamp(),
      });

      return token;
    } catch (error) {
      console.error("Error creating reset token:", error);
      throw new Error("Failed to create reset token");
    }
  }

  /**
   * Validate a password reset token. Returns email if valid, throws if invalid/expired.
   */
  async validatePasswordResetToken(token) {
    try {
      const q = query(
        collection(db, "password_reset_tokens"),
        where("token", "==", token),
        where("used", "==", false),
      );
      const snap = await getDocs(q);
      if (snap.empty) throw new Error("Invalid or already used reset link.");

      const data = snap.docs[0].data();
      const expiresAt =
        data.expiresAt instanceof Date
          ? data.expiresAt
          : (data.expiresAt?.toDate?.() ?? new Date(0));

      if (new Date() > expiresAt) {
        throw new Error(
          "This reset link has expired. Please request a new one.",
        );
      }

      return { email: data.email, docId: snap.docs[0].id };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a registration record by email address.
   */
  async getRegistrationByEmail(email) {
    try {
      const q = query(
        collection(db, "registrations"),
        where("userInfo.email", "==", email),
      );
      const snap = await getDocs(q);
      if (snap.empty) return null;
      return { id: snap.docs[0].id, ...snap.docs[0].data() };
    } catch (error) {
      console.error("Error fetching registration by email:", error);
      return null;
    }
  }

  /**
   * Get a registration record by mobile number.
   * Tries multiple number format variants to handle +251, 251, and 0-prefix formats.
   */
  async getRegistrationByPhone(phone) {
    try {
      const raw = phone.trim().replace(/[\s\-().]/g, "");

      // Build all variants to try
      const variants = new Set();
      variants.add(raw); // as entered

      let n = raw;
      if (n.startsWith("+")) n = n.slice(1);
      variants.add(n); // strip +
      if (/^0\d{9}$/.test(n)) {
        variants.add("251" + n.slice(1)); // 0911... → 251911...
        variants.add("+251" + n.slice(1)); // 0911... → +251911...
      }
      if (/^251\d{9}$/.test(n)) {
        variants.add("0" + n.slice(3)); // 251911... → 0911...
        variants.add("+" + n); // 251911... → +251911...
      }
      if (/^\+251\d{9}$/.test(raw)) {
        variants.add(raw.slice(1)); // +251911... → 251911...
        variants.add("0" + raw.slice(4)); // +251911... → 0911...
      }

      for (const num of variants) {
        const q = query(
          collection(db, "registrations"),
          where("userInfo.mobileNumber", "==", num),
        );
        const snap = await getDocs(q);
        if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
      }
      return null;
    } catch (error) {
      console.error("Error fetching registration by phone:", error);
      return null;
    }
  }

  /**
   * Store or update the SMS message ID on a registration document.
   * Used to track delivery status via the smsethiopia.com API.
   * @param {string} registrationId
   * @param {string} smsMessageId - ULID returned by smsethiopia.com
   */
  async updateRegistrationSmsId(registrationId, smsMessageId) {
    try {
      const docRef = doc(db, "registrations", registrationId);
      await updateDoc(docRef, {
        smsMessageId,
        smsSentAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating SMS message ID:", error);
      // Non-fatal — don't throw, just log
    }
  }

  /**
   * Check if a username is already taken (case-insensitive)
   * Checks both registrations and user_accounts collections.
   * @param {string} username
   * @returns {Promise<boolean>}
   */
  async checkUsernameExists(username) {
    try {
      if (!username) return false;
      const lower = username.toLowerCase().trim();

      // Check registrations
      const q1 = query(
        collection(db, "registrations"),
        where("userInfo.usernameLower", "==", lower),
      );
      const snap1 = await getDocs(q1);
      if (!snap1.empty) return true;

      // Check user_accounts (already-approved users)
      const q2 = query(
        collection(db, "user_accounts"),
        where("usernameLower", "==", lower),
      );
      const snap2 = await getDocs(q2);
      return !snap2.empty;
    } catch (error) {
      console.error("Error checking username existence:", error);
      return false; // Non-fatal — fail open to not block registration
    }
  }

  /**
   * Look up a user_accounts document by username (case-insensitive).
   * Returns the account object (with .email) or null.
   */
  async getUserAccountByUsername(username) {
    try {
      const lower = username.toLowerCase().trim();
      const q = query(
        collection(db, "user_accounts"),
        where("usernameLower", "==", lower),
      );
      const snap = await getDocs(q);
      if (snap.empty) return null;
      return { id: snap.docs[0].id, ...snap.docs[0].data() };
    } catch (error) {
      console.error("Error fetching user account by username:", error);
      return null;
    }
  }

  /**
   * Store a new password after reset/change.
   * Uses a separate `currentPassword` field — never overwrites `temporaryPassword`
   * which is the original one-time credential used for Firebase Auth sign-in lookups.
   */
  async storeNewPassword(email, newPassword) {
    try {
      const docRef = doc(db, "user_accounts", email);
      await updateDoc(docRef, {
        currentPassword: newPassword, // new field — tracks the current password
        passwordChanged: true,
        updatedAt: serverTimestamp(),
        // NOTE: temporaryPassword is intentionally NOT updated here.
        // It's the original credential used for Firebase Auth sign-in during resets.
      });
    } catch (error) {
      console.error("Error storing new password:", error);
      throw new Error("Failed to update password.");
    }
  }

  /**
   * Mark a reset token as used after successful password reset.
   */
  async consumePasswordResetToken(docId) {
    await updateDoc(doc(db, "password_reset_tokens", docId), { used: true });
  }
}

export default new FirestoreService();
